import { inject, Injectable } from '@angular/core';
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { TelemetryLoggerService } from '../../core/services/telemetry/telemetry-logger.service';
import {
  buildLocalStorageKeyMaterial,
  deriveLocalStorageEncryptionKey,
} from '../utility/local-storage-crypto.util';
import {
  clearEncryptedStorageReadCache,
} from '../utility/local-storage-encryption.util';
import { migrateAllPlainLocalStorageKeys } from '../utility/local-storage-native.util';
import {
  isLocalStorageEncryptionKeyReady,
  setLocalStorageEncryptionKey,
} from '../utility/local-storage-key-state.util';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageKeyService {
  private readonly telemetryLogger = inject(TelemetryLoggerService);
  private readonly encryptionLogger =
    this.telemetryLogger.forContext('LocalStorageEncryption');

  /**
   * Key 1.0.0 — Create secret password when user logs in.
   *
   * Parameter account  → Microsoft login account from auth.service (has homeAccountId, localAccountId)
   * Parameter clientId → app id from environment.clientId (passed by auth.service)
   *
   * Steps:
   *   1. Key 1.0.1 — join account ids + clientId into one text
   *   2. Key 1.0.2 — convert text to secret password
   *   3. Key 1.0.3 — save password in app memory
   *   4. Convert any old plain localStorage data to encrypted
   *
   * Called from: auth.service after successful Microsoft login
   */
  public initializeFromAccount(account: AccountInfo, clientId: string): void {
    // Key 1.0.1 — account ids from Microsoft login, clientId from environment
    const material = buildLocalStorageKeyMaterial(
      account.homeAccountId ?? '',
      account.localAccountId ?? '',
      clientId
    );
    // Key 1.0.2 make password → Key 1.0.3 save in memory
    setLocalStorageEncryptionKey(deriveLocalStorageEncryptionKey(material));
    // Encrypt any old plain keys still in browser
    migrateAllPlainLocalStorageKeys();
  }

  /**
   * Key 1.0.6 — Create secret password using Microsoft login library.
   *
   * Parameter msalInstance → Microsoft login client from auth.service
   * Parameter clientId     → app id from environment.clientId
   *
   * Finds logged-in user, then calls initializeFromAccount.
   * Returns true if user found, false if not logged in.
   */
  public initializeFromMsalInstance(
    msalInstance: IPublicClientApplication,
    clientId: string
  ): boolean {
    try {
      // Get active Microsoft account, or first account in list
      const account =
        msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

      // No logged-in user
      if (!account) {
        return false;
      }

      // Create password from this account
      this.initializeFromAccount(account, clientId);
      return true;
    } catch (error) {
      this.encryptionLogger.warn('MSAL encryption key init failed', {
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  }

  /**
   * Key 1.0.7 — Check if secret password is ready for localStorage encryption.
   * No parameters. Returns true if user logged in and password in memory.
   */
  public isEncryptionKeyReady(): boolean {
    return isLocalStorageEncryptionKeyReady();
  }

  /**
   * Migration 4.0.6 — Convert remaining plain localStorage keys to encrypted.
   * No parameters. Called from app.component after navigation.
   */
  public migratePlainKeys(): void {
    migrateAllPlainLocalStorageKeys();
  }

  /**
   * Key 1.0.8 — On logout, remove secret password from memory.
   *
   * No parameters. Called from auth.service on logout.
   * localStorage data stays in browser but cannot be read until next login.
   */
  public clear(): void {
    // Clear password from memory
    setLocalStorageEncryptionKey(null);
    // Clear cached read values
    clearEncryptedStorageReadCache();
  }
}
